import { useReducer, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";
import { API_BASE } from "@/lib/api";

interface SearchResult {
  appid: number;
  name: string;
  image: string;
  price: { final: number; initial: number; discount: number } | null;
}

const NAV_ITEMS = [
  { key: "popular", label: "인기", path: "/" },
  { key: "deals", label: "할인", path: "/deals" },
  { key: "store", label: "스토어", path: "/store" },
  { key: "free", label: "무료", path: "/free" },
  { key: "priceCompare", label: "가격비교", path: "/price-compare" },
];

const MORE_ITEMS = [
  { key: "calendar", label: "캘린더", path: "/calendar" },
  { key: "releases", label: "릴리즈", path: "/releases" },
  { key: "tags", label: "태그", path: "/tags" },
  { key: "compare", label: "비교", path: "/compare" },
];

interface State {
  menuOpen: boolean;
  moreOpen: boolean;
  searchOpen: boolean;
  searchQuery: string;
  searchResults: SearchResult[];
  searching: boolean;
}

type Action =
  | { type: "TOGGLE_MENU" }
  | { type: "CLOSE_MENU" }
  | { type: "TOGGLE_MORE" }
  | { type: "CLOSE_MORE" }
  | { type: "OPEN_SEARCH" }
  | { type: "CLOSE_SEARCH" }
  | { type: "SET_QUERY"; payload: string }
  | { type: "SET_RESULTS"; payload: SearchResult[] }
  | { type: "SET_SEARCHING"; payload: boolean };

const init: State = { menuOpen: false, moreOpen: false, searchOpen: false, searchQuery: "", searchResults: [], searching: false };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "TOGGLE_MENU": return { ...state, menuOpen: !state.menuOpen };
    case "CLOSE_MENU": return { ...state, menuOpen: false };
    case "TOGGLE_MORE": return { ...state, moreOpen: !state.moreOpen };
    case "CLOSE_MORE": return { ...state, moreOpen: false };
    case "OPEN_SEARCH": return { ...state, searchOpen: true };
    case "CLOSE_SEARCH": return { ...state, searchOpen: false, searchQuery: "", searchResults: [] };
    case "SET_QUERY": return { ...state, searchQuery: action.payload };
    case "SET_RESULTS": return { ...state, searchResults: action.payload, searching: false };
    case "SET_SEARCHING": return { ...state, searching: action.payload };
    default: return state;
  }
}

export default function Header() {
  const { theme, cycle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, init);
  const { menuOpen, moreOpen, searchOpen, searchQuery, searchResults, searching } = state;
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputDesktopRef = useRef<HTMLInputElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeSearch = useCallback(() => dispatch({ type: "CLOSE_SEARCH" }), []);

  // Search debounce
  useEffect(() => {
    if (!searchQuery.trim()) { dispatch({ type: "SET_RESULTS", payload: [] }); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      dispatch({ type: "SET_SEARCHING", payload: true });
      try {
        const res = await fetch(`${API_BASE}/store/game-search?term=${encodeURIComponent(searchQuery)}&lang=ko`);
        const data = await res.json();
        dispatch({ type: "SET_RESULTS", payload: data.items || [] });
      } catch { dispatch({ type: "SET_RESULTS", payload: [] }); }
    }, 300);
  }, [searchQuery]);

  // Click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (searchRef.current && !searchRef.current.contains(target) && dropdownRef.current && !dropdownRef.current.contains(target)) closeSearch();
      if (moreRef.current && !moreRef.current.contains(target)) dispatch({ type: "CLOSE_MORE" });
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [closeSearch]);

  useEffect(() => {
    if (searchOpen) {
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      (isDesktop ? inputDesktopRef : inputRef).current?.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") closeSearch();
    if (e.key === "Enter" && searchResults.length > 0) {
      navigate(`/game/${searchResults[0].appid}`);
      closeSearch();
    }
  }, [searchResults, navigate, closeSearch]);

  const showResults = searchOpen && (searchResults.length > 0 || searching);
  const active = location.pathname === "/" ? "popular" : NAV_ITEMS.find((n) => n.path !== "/" && location.pathname.startsWith(n.path))?.key || MORE_ITEMS.find((n) => location.pathname.startsWith(n.path))?.key || "";

  return (
    <>
      <header className="bg-card-bg dark:bg-card-bg border-b border-gray-200 dark:border-card-border sticky top-0 z-50 shadow-sm safe-area-top">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className={`flex items-center gap-4 sm:gap-8 ${searchOpen ? "lg:flex-1" : ""}`}>
            <Link to="/" className="flex items-center gap-2">
              <span className="text-[15px] font-bold text-gray-900 dark:text-white tracking-tight">SteamDeal</span>
            </Link>

            {/* PC full-width search */}
            {searchOpen && (
              <div className="hidden lg:flex flex-1 items-center gap-3 ml-2" ref={searchRef}>
                <div className="relative flex-1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-sub-text pointer-events-none"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  <input ref={inputDesktopRef} type="text" value={searchQuery} onChange={(e) => dispatch({ type: "SET_QUERY", payload: e.target.value })} onKeyDown={handleKeyDown} placeholder="게임 검색..." className="w-full bg-inner-bg dark:bg-inner-bg border border-gray-200 dark:border-card-border rounded-lg pl-9 pr-3 py-1.5 text-sm text-gray-900 dark:text-foreground placeholder-gray-400 dark:placeholder-sub-text focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-[#2a3f5f] focus:border-gray-300 dark:focus:border-[#2a3f5f]" />
                </div>
                <button onClick={closeSearch} className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg hover:bg-hover-bg transition-colors text-gray-500 dark:text-muted">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
            )}

            <nav className={`hidden sm:flex items-center gap-1 ${searchOpen ? "lg:!hidden" : ""}`}>
              {NAV_ITEMS.map((item) => (
                <Link key={item.key} to={item.path} className={`px-3 py-1.5 rounded-md text-sm transition-colors ${active === item.key ? "text-gray-900 dark:text-white bg-hover-bg dark:bg-background font-semibold" : "text-gray-500 dark:text-muted hover:text-gray-900 dark:hover:text-white hover:bg-hover-bg dark:hover:bg-hover-bg"}`}>{item.label}</Link>
              ))}
              {/* More dropdown */}
              <div ref={moreRef} className="relative">
                <button onClick={() => dispatch({ type: "TOGGLE_MORE" })} className={`px-3 py-1.5 rounded-md text-sm transition-colors flex items-center gap-1 ${MORE_ITEMS.some((m) => m.key === active) ? "text-gray-900 dark:text-white bg-hover-bg dark:bg-background font-semibold" : "text-gray-500 dark:text-muted hover:text-gray-900 dark:hover:text-white hover:bg-hover-bg"}`}>
                  {MORE_ITEMS.find((m) => m.key === active)?.label || "더보기"}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${moreOpen ? "rotate-180" : ""}`}><polyline points="6 9 12 15 18 9" /></svg>
                </button>
                {moreOpen && (
                  <div className="absolute top-full left-0 mt-1 w-40 bg-card-bg dark:bg-card-bg rounded-xl border border-gray-200 dark:border-card-border shadow-lg overflow-hidden z-50">
                    {MORE_ITEMS.map((item) => (
                      <Link key={item.key} to={item.path} onClick={() => dispatch({ type: "CLOSE_MORE" })} className={`block px-4 py-2.5 text-sm transition-colors ${active === item.key ? "text-gray-900 dark:text-white bg-hover-bg dark:bg-background font-semibold" : "text-gray-600 dark:text-muted hover:bg-inner-bg dark:hover:bg-hover-bg"}`}>{item.label}</Link>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </div>

          <div className={`flex items-center gap-1.5 sm:gap-2 ${searchOpen ? "lg:hidden" : ""}`}>
            {/* Theme toggle */}
            <button onClick={cycle} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-hover-bg dark:hover:bg-white/10 transition-colors text-gray-500 dark:text-gray-400" title={theme === "dark" ? "다크 모드" : theme === "light" ? "라이트 모드" : "시스템 테마"}>
              {theme === "system" ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
              ) : theme === "dark" ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
              )}
            </button>

            {/* Search button */}
            <button onClick={() => dispatch({ type: "OPEN_SEARCH" })} className={`w-9 h-9 flex items-center justify-center rounded-lg hover:bg-hover-bg dark:hover:bg-hover-bg transition-colors text-gray-500 dark:text-muted ${searchOpen ? "lg:hidden" : ""}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            </button>

            {/* Mobile search overlay */}
            {searchOpen && (
              <div className="fixed inset-x-0 top-0 h-14 flex items-center px-4 bg-card-bg dark:bg-card-bg z-[60] lg:hidden">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-400 dark:text-muted mr-3"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input ref={inputRef} type="text" value={searchQuery} onChange={(e) => dispatch({ type: "SET_QUERY", payload: e.target.value })} onKeyDown={handleKeyDown} placeholder="게임 검색..." className="flex-1 bg-inner-bg dark:bg-inner-bg border border-gray-200 dark:border-card-border rounded-lg pl-3 pr-3 py-2 text-sm text-gray-900 dark:text-foreground placeholder-gray-400 dark:placeholder-sub-text focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-[#2a3f5f]" />
                <button onClick={closeSearch} className="shrink-0 ml-3 text-gray-400 dark:text-muted hover:text-gray-600 dark:hover:text-foreground">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
            )}

            {/* Mobile hamburger */}
            <button onClick={() => dispatch({ type: "TOGGLE_MENU" })} className="sm:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-hover-bg transition-colors text-gray-600 dark:text-muted">
              {menuOpen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="sm:hidden border-t border-gray-200 dark:border-card-border bg-card-bg dark:bg-card-bg">
            <nav className="px-4 py-2 space-y-1">
              {[...NAV_ITEMS, ...MORE_ITEMS].map((item) => (
                <Link key={item.key} to={item.path} onClick={() => dispatch({ type: "CLOSE_MENU" })} className={`block px-3 py-2.5 rounded-lg text-sm transition-colors ${active === item.key ? "text-gray-900 dark:text-white bg-hover-bg dark:bg-background font-semibold" : "text-gray-600 dark:text-muted hover:bg-inner-bg dark:hover:bg-hover-bg"}`}>{item.label}</Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Search results dropdown */}
      {showResults && (
        <div ref={dropdownRef} className="fixed top-14 inset-x-0 z-50 max-h-[60vh] overflow-y-auto bg-card-bg dark:bg-card-bg border-b border-gray-200 dark:border-card-border shadow-lg">
          <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-2">
            {searching ? (
              <div className="py-6 text-center text-sm text-gray-400 dark:text-sub-text">검색 중...</div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-card-border/50">
                {searchResults.slice(0, 8).map((r) => (
                  <Link key={r.appid} to={`/game/${r.appid}`} onClick={closeSearch} className="flex items-center gap-3 px-2 py-2.5 hover:bg-inner-bg dark:hover:bg-hover-bg rounded-lg transition-colors">
                    <img src={r.image || `https://cdn.cloudflare.steamstatic.com/steam/apps/${r.appid}/header.jpg`} alt={r.name} className="w-[60px] h-[28px] rounded object-cover bg-hover-bg shrink-0" />
                    <span className="text-sm text-gray-900 dark:text-white font-medium flex-1 truncate">{r.name}</span>
                    {r.price && r.price.discount > 0 && <span className="text-[10px] font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20 px-1.5 rounded">-{r.price.discount}%</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
